import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import {
  ActivityCategoriesIdsMap,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  GeoLocation,
  OrganizationalUnit,
  Shift,
  TimelineShiftWithActivities,
} from '@wilson/interfaces';
import { LocationsStoreService } from '@wilson/locations';
import {
  DropZoneEventThrottlerDirective,
  GanttDateToPositionPxPipe,
  ShiftMultiSelectHelperService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
  HorizontalDemarcatorRange,
  generateDummyShift,
} from '@wilson/shift-timeline/services';
import {
  ShiftTimelineSettingsState,
  TimelineRemoveActivityFromShiftV2,
  TimelineRemoveShiftFromUserV2,
  TimelineUpsertActivityV2,
  TimelineUpsertNewShiftV2,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { firstValueFrom } from 'rxjs';
import { TimelineUserRowDropZoneCommonOperationsService } from '../../components/timeline-user-row-shifts/timeline-user-row-common-drop-zone-operations.service';
import { DragAndDropHelperService } from '../../services/drag-and-drop-helper.service';
import { ShiftDrawerService } from '../../services/shift-drawer.service';

@Component({
  selector: 'wilson-new-shift-from-empty-activity-drop-zone',
  imports: [],
  providers: [
    GanttDateToPositionPxPipe,
    TimelineUserRowDropZoneCommonOperationsService,
  ],
  template: '',
  styleUrls: ['./new-shift-from-empty-activity-drop-zone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewShiftFromEmptyActivityDropZoneComponent
  extends DropZoneEventThrottlerDirective
  implements OnInit
{
  private store = inject(Store);
  private locationsStoreService = inject(LocationsStoreService);
  private createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private dragAndDropModeService = inject(DragAndDropModeService);
  private shiftsService = inject(ShiftsService);
  private dragAndDropHelperService = inject(DragAndDropHelperService);
  private readonly timelineUserRowDropZoneCommonOperationsService = inject(
    TimelineUserRowDropZoneCommonOperationsService,
  );
  private readonly injector = inject(Injector);
  private readonly shiftDrawerService = inject(ShiftDrawerService);
  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );

  @Input() userId!: string | undefined;

  private OFFSETX_ADJUSTMENT_RATIO = 15;
  private organizationalUnit!: OrganizationalUnit | undefined;
  private currentDemarcatorRange: HorizontalDemarcatorRange | null = null;
  private mockShift!: TimelineShiftWithActivities;
  location!: GeoLocation;
  SHIFT_DURATION = 3600000;

  ngOnInit(): void {
    this.organizationalUnit = this.store.selectSnapshot(
      AuthState.organizationalUnit,
    );

    if (this.organizationalUnit?.locationId) {
      firstValueFrom(
        this.locationsStoreService.getLocationFromCache(
          this.organizationalUnit.locationId as string,
        ),
      ).then((location) => {
        this.location = location;
      });
    }
  }

  protected onDragOverHandler(offsetX: number): void {
    this.dragAndDropModeService.setDraggingItemId(
      CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
    );

    const adjustedOffsetX =
      offsetX -
      this.store.selectSnapshot(ShiftTimelineSettingsState.pixelWidthPerDay()) /
        this.OFFSETX_ADJUSTMENT_RATIO;
    const shiftPositionData =
      this.dragAndDropHelperService.dragItemDatesAndDisplayRange(
        adjustedOffsetX,
        this.SHIFT_DURATION,
      );
    this.currentDemarcatorRange = shiftPositionData.currentDemarcatorRange;
    if (this.organizationalUnit && this.location && this.userId) {
      const activityCategoryId = ActivityCategoriesIdsMap.TrainDrive;
      this.mockShift = generateDummyShift({
        injector: this.injector,
        orgUnit: this.organizationalUnit,
        location: this.location,
        shiftEndDate: shiftPositionData.itemEndDate,
        shiftStartDate: shiftPositionData.itemStartDate,
        userId: this.userId,
        activityCategoryId,
      });

      this.store.dispatch([
        new TimelineUpsertNewShiftV2({
          shift: this.mockShift,
        }),
        new TimelineUpsertActivityV2(
          this.mockShift
            .activities as ActivityWithCategoryAndLocationsAndReportedDetails[],
          this.mockShift.id,
        ),
      ]);
      this.dragAndDropModeService.updateDemarcatorSettings(
        this.currentDemarcatorRange,
      );
    }
  }

  protected onDragLeaveHandler() {
    if (this.userId) {
      this.store.dispatch(
        new TimelineRemoveShiftFromUserV2({
          userId: this.userId,
          shiftId: CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
        }),
      );
      if (this.mockShift?.activities[0].id) {
        this.store.dispatch(
          new TimelineRemoveActivityFromShiftV2(
            this.mockShift.activities[0].id as string,
            CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
          ),
        );
      }
    }
    this.dragAndDropModeService.setDisplayMovingDemarcatorLine(false);
  }

  protected onDropHandler() {
    if (this.currentDemarcatorRange && this.mockShift && this.userId) {
      this.timelineUserRowDropZoneCommonOperationsService.prepareForNewQuickActivityShiftCreation(
        CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
      );
      this.shiftsService.createShifts([this.mockShift]).subscribe({
        next: async (materializedShifts: Shift[]) => {
          const shifts = await firstValueFrom(
            this.shiftsService.getResolvedShift(
              materializedShifts[0].id as string,
            ),
          );
          this.store.dispatch([
            new TimelineRemoveShiftFromUserV2({
              userId: this.mockShift.userId as string,
              shiftId: this.mockShift.id as string,
            }),
            new TimelineRemoveActivityFromShiftV2(
              this.mockShift.activities[0].id as string,
              this.mockShift.id as string,
            ),
            new TimelineUpsertNewShiftV2({
              shift: {
                ...this.mockShift,
                id: shifts[0].id as string,
                activities: shifts[0].activities,
              },
            }),
            new TimelineUpsertActivityV2(
              this.mockShift
                .activities as ActivityWithCategoryAndLocationsAndReportedDetails[],
              shifts[0].id as string,
            ),
          ]);
          this.timelineUserRowDropZoneCommonOperationsService.setDragCardVisibleAndRefreshShiftValidations(
            this.userId,
          );

          this.shiftMultiSelectHelperService.setSelectedShift([
            {
              id: materializedShifts[0].id as string,
              left: this.currentDemarcatorRange?.left || null,
              right: this.currentDemarcatorRange?.right || null,
              isUnAssignedShift: false,
            },
          ]);
        },
        error: () => {
          this.store.dispatch([
            new TimelineRemoveShiftFromUserV2({
              userId: this.mockShift.userId as string,
              shiftId: this.mockShift.id as string,
            }),
            new TimelineRemoveActivityFromShiftV2(
              this.mockShift.activities[0].id as string,
              this.mockShift.id as string,
            ),
          ]);
          this.timelineUserRowDropZoneCommonOperationsService.handleQuickShiftErrorCleanup(
            CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
          );
        },
        complete: () => {
          this.createNewViaDragAndDropService.removeUpdatingId(
            CreateNewViaDragAndDropService.MOCK_SHIFT_ID,
          );
        },
      });
    }
  }
}
